import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { API_BASE } from "@/lib/api";
import { DataTable, Badge, Field, TextInput } from "@/components/ui-lite";
import { AlertTriangle, CheckCircle2, Clock, Paperclip } from "lucide-react";

function fmtUSD(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n));
}

function defaultRange() {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const from = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { from: fmt(from), to: fmt(prev) };
}

export default function ReportsPage() {
  const [{ from, to }, setRange] = useState(defaultRange());
  const [data, setData] = useState(null);
  const [filterMonth, setFilterMonth] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedMonth, setExpandedMonth] = useState(null);

  useEffect(() => {
    api.get(`/reports/status?from_month=${from}&to_month=${to}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [from, to]);

  const filtered = useMemo(() => {
    let inv = data?.invoices || [];
    if (filterMonth !== "all") inv = inv.filter((r) => r.month === filterMonth);
    if (statusFilter === "verified") inv = inv.filter((r) => r.verified || r.status === "verified");
    else if (statusFilter === "paid") inv = inv.filter((r) => r.status === "paid" && !r.verified);
    else if (statusFilter === "pending") inv = inv.filter((r) => r.status !== "paid" && r.status !== "verified");
    return inv;
  }, [data, filterMonth, statusFilter]);

  const totals = useMemo(() => {
    const t = { invoiced: 0, paid: 0, verified: 0, pending: 0, count: 0 };
    for (const m of data?.months || []) {
      t.count += m.count; t.invoiced += m.invoiced_usd; t.paid += m.paid_usd; t.verified += m.verified_usd; t.pending += m.pending_usd;
    }
    return t;
  }, [data]);

  const tone = (r) => r.verified || r.status === "verified" ? "success" : r.status === "paid" ? "info" : "warning";
  const icon = (r) => r.verified || r.status === "verified" ? <CheckCircle2 size={14} className="inline mr-1"/> : r.status === "paid" ? <Clock size={14} className="inline mr-1"/> : <AlertTriangle size={14} className="inline mr-1"/>;

  return (
    <AppShell
      title="Reports"
      subtitle="Reconciliation status across months — what's pending, paid, verified, and missing"
      actions={
        <div className="flex gap-2 items-end">
          <div><label className="text-xs uppercase tracking-widest text-[var(--wv-text-2)] font-semibold">From</label><input type="month" value={from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="ml-2 h-10 px-3 wv-input font-mono text-sm"/></div>
          <div><label className="text-xs uppercase tracking-widest text-[var(--wv-text-2)] font-semibold">To</label><input type="month" value={to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="ml-2 h-10 px-3 wv-input font-mono text-sm"/></div>
        </div>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="wv-card wv-hover-lift p-6" data-testid="report-metric-invoiced">
          <div className="wv-overline">Invoiced</div>
          <div className="mt-3 wv-metric-value">{fmtUSD(totals.invoiced)}</div>
          <div className="mt-1 text-xs text-[var(--wv-text-3)]">{totals.count} invoices · {from} → {to}</div>
        </div>
        <div className="wv-card wv-hover-lift p-6" data-testid="report-metric-paid">
          <div className="wv-overline">Paid</div>
          <div className="mt-3 wv-metric-value">{fmtUSD(totals.paid)}</div>
        </div>
        <div className="wv-card wv-hover-lift p-6" data-testid="report-metric-verified">
          <div className="wv-overline">Blockchain Verified</div>
          <div className="mt-3 wv-metric-value">{fmtUSD(totals.verified)}</div>
        </div>
        <div className="wv-card wv-hover-lift p-6" data-testid="report-metric-pending">
          <div className="wv-overline">Pending</div>
          <div className="mt-3 wv-metric-value text-[var(--wv-warning)]">{fmtUSD(totals.pending)}</div>
        </div>
      </div>

      {/* Per-month */}
      <div className="mt-8">
        <div className="wv-overline mb-3">Month breakdown · click any row to see per-vendor split</div>
        <DataTable
          testid="report-months-table"
          columns={[
            { key: "month", label: "Month", mono: true },
            { key: "count", label: "# Invoices", mono: true },
            { key: "invoiced_usd", label: "Invoiced", mono: true, render: (r) => fmtUSD(r.invoiced_usd) },
            { key: "paid_usd", label: "Paid", mono: true, render: (r) => fmtUSD(r.paid_usd) },
            { key: "verified_usd", label: "Verified", mono: true, render: (r) => fmtUSD(r.verified_usd) },
            { key: "pending_usd", label: "Pending", mono: true, render: (r) => <span className={r.pending_usd > 0 ? "text-[var(--wv-warning)] font-medium" : ""}>{fmtUSD(r.pending_usd)}</span> },
            { key: "with_attachments", label: "With docs", mono: true, render: (r) => <span><Paperclip size={12} className="inline mr-1"/>{r.with_attachments}/{r.count}</span> },
          ]}
          rows={(data?.months || []).map((m) => ({ ...m, id: m.month }))}
          expandable={(r) => {
            const rows = r.by_vendor || [];
            if (rows.length === 0) return <div className="text-sm text-[var(--wv-text-3)]">No invoices in {r.month}.</div>;
            return (
              <div>
                <div className="wv-overline mb-2">Per-vendor breakdown · {r.month}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[var(--wv-text-3)] text-xs uppercase tracking-widest">
                      <th className="text-left py-1 pr-3">Vendor</th>
                      <th className="text-right py-1 pr-3"># Inv</th>
                      <th className="text-right py-1 pr-3">Invoiced</th>
                      <th className="text-right py-1 pr-3">Paid</th>
                      <th className="text-right py-1 pr-3">Pending</th>
                      <th className="text-left py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v, i) => (
                      <tr key={i} className="border-t border-white/50">
                        <td className="py-1.5 pr-3 font-mono font-medium">{v.provider_code}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{v.invoice_count}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{fmtUSD(v.invoiced_usd)}</td>
                        <td className="py-1.5 pr-3 text-right font-mono text-[var(--wv-success)]">{fmtUSD(v.paid_usd)}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{v.pending_usd > 0 ? <span className="text-[var(--wv-warning)]">{fmtUSD(v.pending_usd)}</span> : fmtUSD(v.pending_usd)}</td>
                        <td className="py-1.5 text-xs font-mono">{(v.statuses || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
          expandedRow={expandedMonth}
          onToggleExpand={(id) => setExpandedMonth((cur) => cur === id ? null : id)}
          actions={(r) => <button onClick={() => setFilterMonth(r.month)} className="text-xs text-[var(--wv-primary)] hover:underline">View</button>}
        />
      </div>

      {/* Invoice table */}
      <div className="mt-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="wv-overline">Invoices</div>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="h-8 px-2 wv-input text-xs font-mono">
            <option value="all">all months</option>
            {(data?.months || []).map((m) => <option key={m.month} value={m.month}>{m.month}</option>)}
          </select>
          <div className="flex gap-1">
            {[["all","all"],["pending","pending"],["paid","paid"],["verified","verified"]].map(([k,v]) => (
              <button key={k} onClick={() => setStatusFilter(v)} className={`px-3 py-1 rounded-full text-xs ${statusFilter === v ? "bg-[var(--wv-primary)] text-white" : "bg-white/70 text-[var(--wv-text-2)] border border-white/60 hover:bg-white"}`}>
                {k}
              </button>
            ))}
          </div>
        </div>
        <DataTable
          testid="report-invoices-table"
          columns={[
            { key: "month", label: "Month", mono: true },
            { key: "provider_code", label: "Provider", mono: true },
            { key: "invoice_number", label: "Invoice #", mono: true, render: (r) => r.invoice_number || <span className="text-[var(--wv-text-3)]">—</span> },
            { key: "total_amount", label: "Amount", mono: true, render: (r) => `${Number(r.total_amount).toFixed(2)} ${r.currency}` },
            { key: "total_amount_usd", label: "USD", mono: true, render: (r) => fmtUSD(r.total_amount_usd) },
            { key: "status", label: "Status", render: (r) => <Badge tone={tone(r)}>{icon(r)}{r.verified ? "verified" : r.status}</Badge> },
            { key: "has_attachment", label: "Doc", render: (r) => r.has_attachment ? <Badge tone="success"><Paperclip size={12} className="inline"/></Badge> : <span className="text-[var(--wv-text-3)]">—</span> },
            { key: "due_date", label: "Due", mono: true, render: (r) => r.due_date || <span className="text-[var(--wv-text-3)]">—</span> },
          ]}
          rows={filtered}
        />
      </div>

      {/* Missing invoices */}
      {(data?.missing_invoices || []).length > 0 && (
        <div className="mt-8">
          <div className="wv-overline mb-3">Missing invoices ({data.missing_invoices.length})</div>
          <div className="wv-card p-6">
            <div className="flex flex-wrap gap-2">
              {data.missing_invoices.slice(0, 200).map((m, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-white/70 text-xs">
                  <AlertTriangle size={12} className="text-[var(--wv-warning)]"/>
                  <span className="font-mono">{m.month}</span>
                  <span className="text-[var(--wv-text-2)]">·</span>
                  <span>{m.provider_code}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
